import { faList } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FilterTab from '../../components/common/FilterTab';
import SearchBox from '../../components/common/SearchBox';
import ToggleViewButton from '../../components/common/ToggleViewbutton';
import FourGrid from '../../components/icons/FourGrid';
import { useAdvanceTableContext } from '../../providers/AdvanceTableProvider';
import { useMemo } from 'react';
import { getJobStatusUI } from '../../helpers/jobStatus';
import { Col, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

const ProjectsTopSection = ({ activeView }) => {
  const navigate = useNavigate();
  const { setGlobalFilter, getPrePaginationRowModel, getColumn } =
    useAdvanceTableContext();

  const handleFilterItemClick = (columnId, value) => {
    const column = getColumn(columnId);
    column?.setFilterValue(value === 'all' ? '' : value);
  };

  /**
   * Tabs built from the statuses that are actually on the table.
   *
   * They used to be a fixed list — Ongoing / Pending / Inactive / Critical —
   * written against a status vocabulary this table does not use. Nothing ever
   * carries the label "ongoing", so clicking it filtered every row away, and
   * "ongoing" is precisely what somebody clicks to find a job that is running.
   * A product would vanish from the Projects table and look deleted.
   *
   * Inactive and Critical were never written by anything either, and the counts
   * all read 0 because they were reading `.label` off a status that is a plain
   * string here — this table takes raw jobs, not the adapted DTO.
   *
   * Derived from the rows, so a tab cannot exist without something behind it
   * and a new status gets one for free.
   */
  const tabItems = useMemo(() => {
    const rows = getPrePaginationRowModel().rows;

    const counts = new Map();
    for (const r of rows) {
      const label = getJobStatusUI(r.original?.status).label;
      counts.set(label, (counts.get(label) || 0) + 1);
    }

    return [
      {
        label: 'All',
        value: 'all',
        onClick: () => handleFilterItemClick('status', 'all'),
        count: rows.length
      },
      ...[...counts.entries()]
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .map(([label, count]) => ({
          label: label.charAt(0).toUpperCase() + label.slice(1),
          value: label,
          onClick: () => handleFilterItemClick('status', label),
          count
        }))
    ];
  }, [getPrePaginationRowModel]);

  const handleSearchInputChange = e => {
    setGlobalFilter(e.target.value || undefined);
  };

  return (
    <Row className="g-3 justify-content-between align-items-center mb-4">
      <Col xs={12} sm="auto">
        <FilterTab tabItems={tabItems} handleClick={handleFilterItemClick} />
      </Col>
      <Col xs={12} sm="auto">
        <div className="d-flex align-items-center gap-1">
          <SearchBox
            onChange={handleSearchInputChange}
            placeholder="Search projects"
            style={{ maxWidth: '30rem' }}
            className="me-3"
          />
          <ToggleViewButton
            tooltip="List view"
            active={activeView === 'list'}
            onClick={() => {
              navigate('/apps/project-management/project-list-view');
            }}
          >
            <FontAwesomeIcon icon={faList} className="fs-10" />
          </ToggleViewButton>

          <ToggleViewButton
            tooltip="Card view"
            active={activeView === 'card'}
            onClick={() => {
              navigate('/apps/project-management/project-card-view');
            }}
          >
            <FourGrid />
          </ToggleViewButton>
        </div>
      </Col>
    </Row>
  );
};

export default ProjectsTopSection;
